import { AggregationMetrics } from 'types/api-responses';
import { EurTable } from './decline-curve-analysis/eur-table';

/** One selected forecast and the metrics it produced. */
export interface ForecastMetrics {
  /** The forecast version's name, shown above its figures. */
  label: string;
  metrics: AggregationMetrics | null;
}

export interface WellAnalysisEurSummaryProps {
  forecasts: ForecastMetrics[];
  /**
   * Metrics for the selected production on its own. The recovered volumes come
   * from here, since they are what the well produced whichever forecast is
   * selected.
   */
  productionMetrics: AggregationMetrics | null;
  isLoading?: boolean;
}

/**
 * Production and forecast figures, laid out the way the DCA section lays out
 * its EUR figures: one block per stream, with recovered, remaining, EUR and
 * the stream's ratios.
 *
 * Each selected forecast gets its own set of figures under a heading naming
 * its version. With no forecast selected, only the production figures show.
 *
 * @param {WellAnalysisEurSummaryProps} props - The forecast and production metrics.
 * @return {JSX.Element} The figures.
 */
export const WellAnalysisEurSummary = ({
  forecasts,
  productionMetrics,
  isLoading = false,
}: WellAnalysisEurSummaryProps) => {
  const columns =
    forecasts.length === 0
      ? [{ label: 'Production', metrics: combine(null, productionMetrics) }]
      : forecasts.map(({ label, metrics }) => ({
          label,
          metrics: combine(metrics, productionMetrics),
        }));

  return <EurTable columns={columns} isLoading={isLoading} />;
};

/**
 * Recovered volumes come from the production metrics and everything else from
 * the forecast's, matching how the DCA section fills the same table.
 *
 * @param {AggregationMetrics | null} forecastMetrics - The forecast's metrics.
 * @param {AggregationMetrics | null} productionMetrics - The production's metrics.
 * @return {object | null} The figures the table shows, or null when there are none.
 */
const combine = (
  forecastMetrics: AggregationMetrics | null,
  productionMetrics: AggregationMetrics | null
) => {
  if (!forecastMetrics && !productionMetrics) return null;

  const source = forecastMetrics ?? productionMetrics;

  return {
    recoveredOil: productionMetrics?.recoveredOil ?? null,
    recoveredGas: productionMetrics?.recoveredGas ?? null,
    recoveredWater: productionMetrics?.recoveredWater ?? null,
    oilEur: source?.oilEur ?? null,
    gasEur: source?.gasEur ?? null,
    waterEur: source?.waterEur ?? null,
    remainingOil: source?.remainingOil ?? null,
    remainingGas: source?.remainingGas ?? null,
    remainingWater: source?.remainingWater ?? null,
    gasOilRatioTotal: source?.gasOilRatioTotal ?? null,
    waterCutTotal: source?.waterCutTotal ?? null,
    waterGasRatioTotal: source?.waterGasRatioTotal ?? null,
    oilGasRatioTotal: source?.oilGasRatioTotal ?? null,
    waterOilRatioTotal: source?.waterOilRatioTotal ?? null,
  };
};
